import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { AlertCircle, FileText, Upload, Check, X, File } from 'lucide-react';
import { storage } from '@/lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '@/components/AuthProvider';
import { useToast } from '@/components/ui/use-toast';

interface DocumentationStepProps {
  onSubmit: (data: any) => void;
  onPrevious?: () => void;
  initialData?: any;
  getEnabledDocuments?: () => any[];
  getRequiredDocuments?: (monthlyVolume?: number) => any[];
}

interface UploadedFile {
  name: string;
  url: string;
  uploadedAt: Date;
}

interface DocumentUploads {
  [documentType: string]: UploadedFile[];
}

const ALLOWED_FILE_TYPES = {
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'application/pdf': '.pdf',
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export function DocumentationStep({ onSubmit, onPrevious, initialData, getEnabledDocuments, getRequiredDocuments }: DocumentationStepProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState<{ [key: string]: boolean }>({});
  // Get enabled documents based on configuration
  const enabledDocs = getEnabledDocuments ? getEnabledDocuments() : [
    { documentType: 'bank_statements', label: 'Bank Statements', required: true },
    { documentType: 'drivers_license', label: "Driver's License", required: true },
    { documentType: 'voided_check', label: 'Voided Check', required: true }
  ];

  // Initialize uploads state dynamically based on enabled documents
  const initialUploads = enabledDocs.reduce((acc, doc) => {
    acc[doc.documentType] = initialData?.[doc.documentType] || [];
    return acc;
  }, {} as DocumentUploads);

  const [uploads, setUploads] = useState<DocumentUploads>(initialUploads);

  // Refs are now handled dynamically via document.getElementById

  const validateFile = (file: File): string | null => {
    if (!Object.keys(ALLOWED_FILE_TYPES).includes(file.type)) {
      return 'File type not allowed. Please upload JPG, PNG, or PDF files only.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size too large. Maximum size is 10MB.';
    }
    return null;
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    documentType: string
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading({ ...isUploading, [documentType]: true });

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        // Validate file
        const error = validateFile(file);
        if (error) {
          toast({
            title: 'Invalid file',
            description: error,
            variant: 'destructive',
          });
          return null;
        }

        // Create storage reference
        const timestamp = Date.now();
        const fileName = `merchant-applications/${user?.companyId}/${documentType}/${timestamp}_${file.name}`;
        const storageRef = ref(storage, fileName);

        // Upload file
        const snapshot = await uploadBytes(storageRef, file);
        const downloadUrl = await getDownloadURL(snapshot.ref);

        return {
          name: file.name,
          url: downloadUrl,
          uploadedAt: new Date(),
        };
      });

      const uploadedFiles = (await Promise.all(uploadPromises)).filter(Boolean) as UploadedFile[];

      // Update state with new uploads
      setUploads((prev) => ({
        ...prev,
        [documentType]: [...prev[documentType], ...uploadedFiles],
      }));

      toast({
        title: 'Files uploaded',
        description: `Successfully uploaded ${uploadedFiles.length} file(s)`,
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: 'Failed to upload files. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading({ ...isUploading, [documentType]: false });
      // Reset input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const removeFile = (documentType: string, index: number) => {
    setUploads((prev) => ({
      ...prev,
      [documentType]: prev[documentType].filter((_, i) => i !== index),
    }));
  };

  const onFormSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      // Get required documents based on processing volume
      const monthlyVolume = initialData?.processingHistory?.monthlyVolume;
      const requiredDocs = getRequiredDocuments ? getRequiredDocuments(monthlyVolume) : enabledDocs.filter(doc => doc.required);
      
      // Validate required documents
      for (const doc of requiredDocs) {
        const docType = doc.documentType;
        if (!uploads[docType] || uploads[docType].length === 0) {
          toast({
            title: 'Missing documents',
            description: `Please upload ${doc.label}`,
            variant: 'destructive',
          });
          return;
        }
      }
      
      await onSubmit(uploads);
    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: 'Error',
        description: 'Failed to save documents. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFileList = (files: UploadedFile[], documentType: string) => {
    if (files.length === 0) return null;

    return (
      <div className="mt-2 space-y-2">
        {files.map((file, index) => (
          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
            <div className="flex items-center gap-2">
              <File className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-700">{file.name}</span>
              <Check className="h-4 w-4 text-green-500" />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeFile(documentType, index)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Documentation
        </CardTitle>
        <CardDescription>
          Upload the required documents to complete your application. All documents must be clear and legible.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            All uploaded documents are encrypted and stored securely. Accepted formats: JPG, PNG, PDF (max 10MB per file).
          </AlertDescription>
        </Alert>

        {/* Dynamic Document Sections */}
        {enabledDocs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No documents required for this application.
          </div>
        ) : (
          enabledDocs.map((doc) => {
            const monthlyVolume = initialData?.processingHistory?.monthlyVolume;
            const requiredDocs = getRequiredDocuments ? getRequiredDocuments(monthlyVolume) : enabledDocs.filter(d => d.required);
            const isRequired = requiredDocs.some(reqDoc => reqDoc.documentType === doc.documentType);
            
            return (
              <div key={doc.documentType} className="space-y-2">
                <Label htmlFor={doc.documentType}>
                  {doc.label} {isRequired && <span className="text-red-500">*</span>}
                </Label>
                <p className="text-sm text-gray-600">
                  {doc.description || `Upload your ${doc.label.toLowerCase()}`}
                </p>
                <input
                  id={doc.documentType}
                  type="file"
                  multiple
                  accept={doc.acceptedFormats ? doc.acceptedFormats.map(f => `.${f}`).join(',') : '.jpg,.jpeg,.png,.pdf'}
                  onChange={(e) => handleFileUpload(e, doc.documentType)}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById(doc.documentType)?.click()}
                  disabled={isUploading[doc.documentType]}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {isUploading[doc.documentType] ? 'Uploading...' : `Upload ${doc.label}`}
                </Button>
                {renderFileList(uploads[doc.documentType] || [], doc.documentType)}
              </div>
            );
          })
        )}

        {/* Form Actions */}
        <div className="flex justify-between pt-6">
          {onPrevious && (
            <Button type="button" variant="outline" onClick={onPrevious}>
              Previous
            </Button>
          )}
          <Button onClick={onFormSubmit} disabled={isSubmitting} className="ml-auto">
            {isSubmitting ? 'Submitting...' : 'Complete Setup'}
          </Button>
        </div>
      </CardContent>
    </div>
  );
}